import { api, ensureCsrfCookie } from '@/api/client';
import type { NotificationEvent } from '@/types/generated';

/**
 * The bell.
 *
 * Scoped to the active organisation by the server, against the id every notification carries on
 * its payload — `notifications` has no tenant column because an inbox belongs to a person, not to
 * a company. Nothing here passes an organisation: the header the client already sends decides it,
 * exactly as for every other endpoint.
 */

export interface NotificationItem {
  id: string;
  /**
   * Deliberately widened from the generated union. A stored notification keeps whatever event it
   * was written with, so a row predating a removed case is a real value the enum no longer has.
   */
  event: string | null;
  title: string;
  body: string;
  url: string | null;
  read_at: string | null;
  created_at: string | null;
}

export interface NotificationPage {
  data: NotificationItem[];
  meta: { unread_count: number };
}

export interface NotificationPreference {
  /** The live set, unlike a stored notification's — the server builds this from the enum. */
  event: NotificationEvent;
  label: string;
  description: string;
  email: boolean;
  /** True while the user has never chosen — the value shown is the system default. */
  is_default: boolean;
}

export async function fetchNotifications(limit = 20): Promise<NotificationPage> {
  const { data } = await api.get<NotificationPage>('/notifications', { params: { limit } });
  return data;
}

/**
 * Just the badge.
 *
 * Its own endpoint because this is polled and the list is not. Returning twenty rendered rows
 * every thirty seconds to display one integer is waste that only shows up on someone else's
 * connection.
 */
export async function fetchUnreadCount(): Promise<number> {
  const { data } = await api.get<{ unread_count: number }>('/notifications/unread-count');
  return data.unread_count;
}

export async function markNotificationRead(id: string): Promise<void> {
  await ensureCsrfCookie();
  await api.post(`/notifications/${id}/read`);
}

export async function markAllNotificationsRead(): Promise<void> {
  await ensureCsrfCookie();
  await api.post('/notifications/read-all');
}

export async function fetchNotificationPreferences(): Promise<NotificationPreference[]> {
  const { data } = await api.get<{ data: NotificationPreference[] }>('/notification-preferences');
  return data.data;
}

export async function updateNotificationPreference(
  event: NotificationEvent,
  email: boolean,
): Promise<NotificationPreference[]> {
  await ensureCsrfCookie();
  const { data } = await api.patch<{ data: NotificationPreference[] }>(
    '/notification-preferences',
    { event, email },
  );
  return data.data;
}
